import awsRumStatusTypes from '../../consts/aws/awsRumStatusTypes';
import damRequestsTimeouts from '../../consts/dam/damRequestsTimeouts';
import autoStartStates from '../../consts/device/settings/autoStartStates';
import deviceLockTypes from '../../consts/device/settings/deviceLockTypes';
import flexBatteryModeTypes from '../../consts/device/settings/flexBatteryModeTypes';
import flexPuffModeStatuses from '../../consts/device/settings/flexPuffModeStatuses';
import hapticDelayValues from '../../consts/device/settings/hapticDelayValues';
import hapticProfileValues from '../../consts/device/settings/hapticProfileValues';
import pauseModeStatuses from '../../consts/device/settings/pauseModeStatuses';
import settingNames from '../../consts/device/settings/settingNames';
import uiBatteryChargedVibrationValues from '../../consts/device/settings/uiBatteryChargedVibrationValues';
import vibrationNames from '../../consts/device/settings/vibrationNames';
import milestoneTypes from '../../consts/loyalty/milestoneTypes';
import {strCode} from '../../consts/strCode';
import RequestCachedError from '../../errors/requestCachedError';
import {selectIsOnline} from '../../state/selectors/global';
import {
    selectBatteryCharged,
    selectCurrentDusn,
    selectCurrentHolderDusn,
    selectDeviceFlexBatteryMode,
    selectDevicePauseMode,
    selectDeviceSetting,
    selectDeviceUpdateSettingsInProgress,
    selectHeatingVibrations,
    selectIotDeviceConnected,
    selectIsDeviceConnectInProgress,
    selectIsDeviceReconnectInProgress,
    selectIsDeviceSettingLocked,
    selectIsDeviceSyncInProgress,
    selectIsDeviceUpdateSettingInProgress,
} from '../../state/selectors/iotDevices';
import {setIsDeviceUpdateSettingInProgress, updateDeviceSettings} from '../../state/slice/iotDevices';
import {dispatch, getState} from '../../state/store';
import valueUtils from '../../utils/valueUtils';
import rumCustomEvents from '../events/rumCustomEvents';
import rumCustomEventsHelpers from '../events/rumCustomEventsHelpers';
import log from '../logger/log';
import loyaltyService from '../loyalty/loyaltyService';
import notificationService from '../notificationService';
import uamfErrorHelpers from '../uamf/uamfErrorHelpers';
import damAsyncRequestService from './damAsyncRequestService';
import damClientService from './damClientService';

const offlinePrevSettingValues = new Map();

const getOfflinePrevSettingKey = (dusn, settingName) => `${dusn}:${settingName}`;

const rememberOfflinePrevSettingValue = (dusn, settingName, prevSettingValue) => {
    const key = getOfflinePrevSettingKey(dusn, settingName);

    // a repeated offline change overwrites the cached request, but the device still has the first value
    if (!offlinePrevSettingValues.has(key)) {
        offlinePrevSettingValues.set(key, prevSettingValue);
    }
};

const handleUpdateSettingError = ({dusn, settingName, settingValue, prevSettingValue, error, startParameters}) => {
    dispatch(updateDeviceSettings(dusn, settingName, prevSettingValue));

    if (uamfErrorHelpers.checkIsActiveRequestAlreadyExistsError(dusn, error)) {
        log.warn(`damDeviceSettingsService: Update rejected. Active request already exists: ${settingName}`, dusn);

        notificationService.showActiveRequestAlreadyExistsWarning(dusn);
        return;
    }

    rumCustomEvents.pushSettings({
        dusn,
        settingName,
        settingValue,
        eventId: error?.requestId,
        status: awsRumStatusTypes.FAILED,
        ...startParameters,
    });

    log.error(`damDeviceSettingsService: Failed to update: ${settingName}`, error?.message, dusn);

    notificationService.showToastNotification({messageStr: strCode.DEVICE_SETTINGS_PAGE_DEFAULT_ERROR, dusn});
};

const updateDeviceSetting = async ({dusn, settingName, payload, settingValue, shouldNotificationShow = true}) => {
    if (!dusn) {
        // kit not fully paired — can't resolve holder dusn
        notificationService.showReconnectDeviceWarning(selectCurrentDusn(getState()));
        return;
    }

    let actualSettingValue = settingValue;

    if (!actualSettingValue) {
        [actualSettingValue] = Object.values(payload);
    }

    const prevSettingValue = selectDeviceSetting(dusn, settingName)(getState());
    const isAvailable = checkIsUpdateDeviceSettingAvailable(dusn, settingName, shouldNotificationShow);

    if (!isAvailable) return;

    const isUnchangedValue = prevSettingValue === actualSettingValue;

    if (isUnchangedValue) {
        log.info(`damDeviceSettingsService: Skip update: ${settingName}, Value is unchanged`, dusn);
        return;
    }

    const startParameters = rumCustomEventsHelpers.getStartParameters();

    dispatch(setIsDeviceUpdateSettingInProgress(dusn, settingName, true));

    try {
        log.info(`damDeviceSettingsService: Start updating: ${settingName}, value: ${JSON.stringify(payload)}`, dusn);

        dispatch(updateDeviceSettings(dusn, settingName, actualSettingValue));

        const {requestId} = await damClientService.postUpdateSetting(dusn, settingName, payload);

        await damAsyncRequestService.waitAsyncRequestComplete(dusn, requestId, damRequestsTimeouts.SETTING);

        log.info(
            `damDeviceSettingsService: Successfully updated: ${settingName}, value: ${JSON.stringify(
                actualSettingValue
            )}`,
            dusn
        );

        // loyalty
        if (settingName === settingNames.CUSTOM_UI) {
            loyaltyService.completeMilestone(milestoneTypes.CHANGE_DEVICE_COLOR);
        }

        rumCustomEvents.pushSettings({
            dusn,
            settingName,
            settingValue: actualSettingValue,
            eventId: requestId,
            status: awsRumStatusTypes.SUCCESS,
            ...startParameters,
        });
    } catch (e) {
        if (e instanceof RequestCachedError) {
            rememberOfflinePrevSettingValue(dusn, settingName, prevSettingValue);

            dispatch(updateDeviceSettings(dusn, settingName, actualSettingValue));

            if (shouldNotificationShow) notificationService.showRequestSavedSuccess();
        } else {
            handleUpdateSettingError({
                dusn,
                settingName,
                settingValue: actualSettingValue,
                prevSettingValue,
                error: e,
                startParameters,
            });
        }
    }

    dispatch(setIsDeviceUpdateSettingInProgress(dusn, settingName, false));
};

// replay of a request cached while offline: the value is already applied in the store,
// so we re-send it, wait for the device confirmation over WSS and roll the value back on failure
const syncUpdateSetting = async (dusn, settingName, newValue) => {
    const key = getOfflinePrevSettingKey(dusn, settingName);
    const startParameters = rumCustomEventsHelpers.getStartParameters();

    try {
        const {requestId} = await damClientService.postUpdateSetting(dusn, settingName, newValue);

        await damAsyncRequestService.waitAsyncRequestComplete(dusn, requestId, damRequestsTimeouts.SETTING);

        log.info(`damDeviceSettingsService: Successfully synced: ${settingName}`, dusn);
    } catch (e) {
        const prevSettingValue = offlinePrevSettingValues.has(key)
            ? offlinePrevSettingValues.get(key)
            : selectDeviceSetting(dusn, settingName)(getState());

        handleUpdateSettingError({
            dusn,
            settingName,
            settingValue: newValue,
            prevSettingValue,
            error: e,
            startParameters,
        });

        throw e;
    } finally {
        offlinePrevSettingValues.delete(key);
    }
};

// clone of the useIsDeviceSettingAvailable
const checkIsDeviceSettingAvailable = (settingName) => {
    const holderDusn = selectCurrentHolderDusn(getState());
    const isSettingLocked = selectIsDeviceSettingLocked(holderDusn, settingName)(getState());

    if (isSettingLocked) {
        log.info(`damDeviceSettingsService: Setting is unavailable. Locked: ${settingName}`, holderDusn);
        notificationService.showDeviceLockWarning();
        return false;
    }

    return true;
};

const checkIsUpdateDeviceSettingAvailable = (dusn, settingName, shouldNotificationShow = true) => {
    const isDeviceSyncInProgress = selectIsDeviceSyncInProgress(dusn)(getState());

    if (isDeviceSyncInProgress) {
        log.info(`damDeviceSettingsService: Update unavailable. Sync in progress: ${settingName}`, dusn);
        notificationService.showDeviceSyncInProgressWarning();
        return false;
    }

    const isDeviceReconnectInProgress = selectIsDeviceReconnectInProgress(dusn)(getState());
    const isDeviceConnectInProgress = selectIsDeviceConnectInProgress(dusn)(getState());

    if (isDeviceConnectInProgress || isDeviceReconnectInProgress) {
        log.info(`damDeviceSettingsService: Update unavailable. Connect in progress: ${settingName}`, dusn);
        notificationService.showDeviceConnectInProgressWarning(dusn);
        return false;
    }

    const isDeviceUpdateSettingInProgress = selectIsDeviceUpdateSettingInProgress(dusn, settingName)(getState());

    if (isDeviceUpdateSettingInProgress) {
        const isSameSettingInProgress = selectDeviceUpdateSettingsInProgress(dusn)(getState())?.includes(settingName);

        if (isSameSettingInProgress) {
            log.info(`damDeviceSettingsService: Update unavailable. Already in progress: ${settingName}`, dusn);
            notificationService.showProcessInProgressWarning();
        } else {
            // Only for i5
            log.info(`damDeviceSettingsService: Update unavailable. Another setting in progress: ${settingName}`, dusn);
            notificationService.showActiveRequestAlreadyExistsWarning(dusn);
        }

        return false;
    }

    const isSettingAvailable = checkIsDeviceSettingAvailable(settingName);

    if (!isSettingAvailable) {
        log.info(`damDeviceSettingsService: Update unavailable. Setting is unavailable: ${settingName}`, dusn);
        return false;
    }

    const isOnline = selectIsOnline(getState());

    if (!isOnline) {
        log.info(`damDeviceSettingsService: Update available. User is offline: ${settingName}`, dusn);
        return true;
    }

    const isDeviceConnected = selectIotDeviceConnected(dusn)(getState());

    if (!isDeviceConnected) {
        log.info(`damDeviceSettingsService: Update unavailable. Device is disconnected: ${settingName}`, dusn);
        if (shouldNotificationShow) notificationService.showReconnectDeviceWarning(dusn);
        return false;
    }

    const deviceSetting = selectDeviceSetting(dusn, settingName)(getState());

    if (!valueUtils.isValidValue(deviceSetting)) {
        log.info(`damDeviceSettingsService: Update unavailable. Value is invalid: ${settingName}`, dusn);
        if (shouldNotificationShow) notificationService.showRefreshDeviceSettingsWarning(dusn);
        return false;
    }

    return true;
};

const updateFlexBattery = async (dusn, value) => {
    const promises = [
        updateDeviceSetting({
            dusn,
            settingName: settingNames.FLEX_BATTERY,
            payload: {mode: value},
        }),
    ];

    const pauseMode = selectDevicePauseMode(dusn)(getState());

    if (value === flexBatteryModeTypes.ECO_MODE && pauseMode === pauseModeStatuses.ENABLED) {
        promises.push(updatePauseMode(dusn, false));
    }

    await Promise.all(promises);
};

const updateChargerCustomUI = ({dusn, value, shouldNotificationShow}) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.CUSTOM_UI,
        payload: {colourScheme: value},
        shouldNotificationShow,
    });

const updateChargerBrightness = ({dusn, value, shouldNotificationShow}) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.BRIGHTNESS,
        payload: {value},
        shouldNotificationShow,
    });

const updateFlexPuff = (dusn, isActive) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.FLEX_PUFF,
        payload: {
            status: isActive ? flexPuffModeStatuses.ENABLED : flexPuffModeStatuses.DISABLED,
        },
    });

const updatePauseMode = async (dusn, isActive) => {
    const promises = [
        updateDeviceSetting({
            dusn,
            settingName: settingNames.PAUSE_MODE,
            payload: {
                status: isActive ? pauseModeStatuses.ENABLED : pauseModeStatuses.DISABLED,
            },
        }),
    ];

    const flexBatteryMode = selectDeviceFlexBatteryMode(dusn)(getState());

    if (flexBatteryMode === flexBatteryModeTypes.ECO_MODE && isActive) {
        promises.push(updateFlexBattery(dusn, flexBatteryModeTypes.PERFORMANCE_MODE));
    }

    await Promise.all(promises);
};

const updateDeviceLock = (dusn, isActive) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.DEVICE_LOCK,
        payload: {
            protectionType: isActive ? deviceLockTypes.ENABLED : deviceLockTypes.DISABLED,
            timeout: 0,
        },
    });

const updateAutoStart = (dusn, isActive) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.AUTO_START,
        payload: {
            featureState: isActive ? autoStartStates.ON : autoStartStates.OFF,
        },
    });

const updateLiftUpGesture = (dusn, isActive) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.LIFT_UP_GESTURE,
        payload: {
            gestureState: isActive ? autoStartStates.ON : autoStartStates.OFF,
        },
    });

const updateLedIntensity = (dusn, type) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.LED_INTENSITY,
        payload: {
            intensity: type,
        },
    });

const updateHeatingVibrations = ({dusn, vibrations, shouldNotificationShow}) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.HEATING_VIBRATION,
        payload: {...vibrations, ...getExtraHeatingVibrationValues(vibrations)},
        settingValue: vibrations,
        shouldNotificationShow,
    });

const updateBatteryCharged = ({dusn, vibrations, shouldNotificationShow}) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.BATTERY_CHARGED,
        payload: vibrations,
        settingValue: vibrations,
        shouldNotificationShow,
    });

const updateDeviceVibrations = async (dusn, isActive) => {
    const heatingVibrations = selectHeatingVibrations(dusn)(getState());
    const uiBatteryCharged = selectBatteryCharged(dusn)(getState());

    const heatingPayload = Object.fromEntries(
        Object.entries(heatingVibrations).map(([key, value]) =>
            typeof value === 'boolean' ? [key, isActive] : [key, value]
        )
    );

    const batteryPayload = Object.fromEntries(
        Object.keys(uiBatteryCharged).map((key) => [
            key,
            !isActive ? uiBatteryChargedVibrationValues.OFF : uiBatteryChargedVibrationValues.ON,
        ])
    );

    await updateBatteryCharged({dusn, vibrations: batteryPayload});

    await updateHeatingVibrations({dusn, vibrations: heatingPayload, shouldNotificationShow: false});
};

const getExtraHeatingVibrationValues = (heatingVibrations) => {
    const isAnyActive = checkIsAnyHeatingVibrationsValueActive(heatingVibrations);

    return {
        [vibrationNames.HAPTIC_PROFILE]: isAnyActive ? hapticProfileValues.ENABLED : hapticProfileValues.DISABLED,
        [vibrationNames.HAPTIC_DELAY]: hapticDelayValues.DISABLED,
    };
};

const validatePercentage = (settingName, value) => {
    if (!Number.isInteger(value) || value < 0 || value > 100) {
        log.warn(`damDeviceSettingsService: Invalid percentage for ${settingName}: ${value}`);
        return false;
    }
    return true;
};

const updateVibrationIntensity = (dusn, value) => {
    if (!validatePercentage(settingNames.VIBRATION_INTENSITY, value)) return;

    return updateDeviceSetting({
        dusn,
        settingName: settingNames.VIBRATION_INTENSITY,
        payload: {percentage: value},
    });
};

const updateTunability = (dusn, isActive) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.TUNABILITY,
        payload: {featureState: isActive ? autoStartStates.ON : autoStartStates.OFF},
    });

const updateRememberHeatingProfile = (dusn, isActive) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.REMEMBER_HEATING_PROFILE,
        payload: {featureState: isActive ? autoStartStates.ON : autoStartStates.OFF},
    });

const updateRangeMode = (dusn, isActive) =>
    updateDeviceSetting({
        dusn,
        settingName: settingNames.RANGE_MODE,
        payload: {featureState: isActive ? autoStartStates.ON : autoStartStates.OFF},
    });

const checkIsAnyBatteryChargedValueActive = (batteryCharged = {}) =>
    Object.values(batteryCharged).some((value) => value === uiBatteryChargedVibrationValues.ON);

const checkIsAnyHeatingVibrationsValueActive = (heatingVibrations = {}) =>
    Object.values(heatingVibrations).some((value) => value);

const checkIsAnyVibrationValueActive = (batteryCharged, heatingVibrations) =>
    checkIsAnyBatteryChargedValueActive(batteryCharged) || checkIsAnyHeatingVibrationsValueActive(heatingVibrations);

export default {
    updateDeviceSetting,
    syncUpdateSetting,
    updateChargerCustomUI,
    updateChargerBrightness,
    checkIsUpdateDeviceSettingAvailable,
    updateFlexBattery,
    updateFlexPuff,
    updatePauseMode,
    updateDeviceLock,
    updateAutoStart,
    updateLiftUpGesture,
    updateLedIntensity,
    updateHeatingVibrations,
    updateBatteryCharged,
    updateDeviceVibrations,
    updateVibrationIntensity,
    updateTunability,
    updateRememberHeatingProfile,
    updateRangeMode,
    validatePercentage,
    checkIsDeviceSettingAvailable,
    checkIsAnyVibrationValueActive,
};
